import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Markdown } from "@vd/ui";
import { bugReportQuery } from "../../../api/admin";
import { ApiError } from "../../../api/client";
import type { BugReportDetail, BugStructuredReport } from "../../../api/types";
import { requireAdmin, useMe } from "../../../auth/useMe";
import { Crumbs } from "../../../components/Crumbs";
import { Badge, Card, EmptyState, ErrorState, Loading } from "../../../components/ui";
import { useReportName } from "../../../lib/crumbLabels";
import { level } from "../../../lib/crumbs";
import { fmtDateTime } from "../../../lib/format";
import { Link } from "../../../lib/navigate";
import {
  EmailBadge,
  KindBadge,
  ReportCode,
  reportCode,
  SourceBadge,
  StatusBadge,
  TranscriptBadge,
} from "./-badges";
import { FixProposal } from "./-fix-proposal";
import { FollowUpCard, ResolutionCard } from "./-follow-up";

export const Route = createFileRoute("/admin/support/$reportId")({
  staticData: {
    crumb: {
      label: "Report",
      useLabel: ({ params }) => useReportName(params.reportId ?? ""),
      parents: [level("Support", "/admin/support")],
    },
  },
  beforeLoad: requireAdmin,
  loader: ({ context, params }) => context.queryClient.prefetchQuery(bugReportQuery(params.reportId)),
  component: BugReportPage,
});

/** The page's crumbs (ADR 0030: from the trail and the route's declaration). */
function BackLink(_props: { title?: string }) {
  return <Crumbs className="mb-2" />;
}

function BugReportPage() {
  const { reportId } = Route.useParams();
  const q = useQuery(bugReportQuery(reportId));
  if (q.isPending) return <Loading />;
  if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <>
        <BackLink />
        {notFound ? (
          <EmptyState title="This report doesn't exist" />
        ) : (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        )}
      </>
    );
  }
  return <ReportView r={q.data} />;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v ? v : null;
}

function ReportView({ r }: { r: BugReportDetail }) {
  // Triage, resolving and Kite's email live on the ops console once it is set up (story CA4).
  const ops = useMe().operator_pages === true;
  const d = r.diagnostics as Record<string, Record<string, unknown> | undefined>;
  const facts: [string, React.ReactNode][] = [
    ["Sent", fmtDateTime(r.created_at)],
    ["User", r.user ? `${r.user.name}${r.user.email ? ` (${r.user.email})` : ""}` : ""],
    ["App version", r.app_version ?? ""],
    ["OS", r.os ?? ""],
    ["Phone", str(d.phone?.status) ?? ""],
    ["Realtime", str(d.realtime?.status) ?? ""],
    ["Microphone", str(d.audio?.mic_label) ?? str(d.audio?.mic_device_id) ?? ""],
    [
      "Call",
      r.call_id ? (
        <Link
          key="c"
          to="/admin/calls/$callId"
          params={{ callId: r.call_id }}
          className="text-accent-ink hover:underline"
        >
          Open call
        </Link>
      ) : (
        ""
      ),
    ],
  ];
  const events = Array.isArray(r.diagnostics.events) ? r.diagnostics.events.length : 0;
  const code = r.number ? reportCode(r.number) : "Report";

  return (
    <>
      <BackLink title={code} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="font-display font-medium text-display">
          {r.report?.title || `Problem report from ${r.user?.name ?? "an unknown user"}`}
        </h1>
        <span className="flex items-center gap-1.5">
          {r.number ? <ReportCode number={r.number} className="text-ui" /> : null}
          <StatusBadge status={r.status} fixedVersion={r.fixed_version} stage={r.stage ?? null} />
          {r.stage_at ? (
            <span className="text-caption text-ink-3 tnum">{fmtDateTime(r.stage_at)}</span>
          ) : null}
          {r.source === "assistant" ? (
            <>
              <SourceBadge severity={r.report?.severity ?? null} kind={r.report?.kind ?? null} />
              <KindBadge kind={r.report?.kind} />
            </>
          ) : (
            <TranscriptBadge status={r.transcript_status} />
          )}
          {ops ? <EmailBadge status={r.email_status} /> : null}
        </span>
        <span className="ml-auto font-mono text-caption text-ink-3" title="Report ID">
          {r.id}
        </span>
      </div>

      <dl className="mb-4 grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg bg-surface-1 px-4 py-3 shadow-material sm:grid-cols-4">
        {facts.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <dt className="text-caption text-ink-2">{k}</dt>
            <dd className="truncate text-ui">{v || <span className="text-ink-3">None</span>}</dd>
          </div>
        ))}
      </dl>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="flex min-w-0 flex-col gap-4">
          {r.report ? (
            <>
              <StructuredCard report={r.report} />
              <ConversationCard r={r} />
            </>
          ) : (
            <RecordingCards r={r} />
          )}
          {r.can_message || r.follow_ups.length > 0 ? <FollowUpCard r={r} /> : null}
          {ops && r.triage ? <FixProposal t={r.triage} /> : null}
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <ResolutionCard r={r} triage={ops} />
          {ops ? (
            <Card title="Email">
              <EmailBody r={r} />
            </Card>
          ) : null}
          <Card title={`Diagnostics, ${events} events`}>
            <pre className="max-h-[560px] overflow-auto px-4 py-3 font-mono text-micro leading-relaxed text-ink">
              {JSON.stringify(r.diagnostics, null, 2)}
            </pre>
          </Card>
        </div>
      </div>
    </>
  );
}

function RecordingCards({ r }: { r: BugReportDetail }) {
  return (
    <>
      <Card title="Recording">
        <div className="px-4 py-3">
          {r.audio_url ? (
            // biome-ignore lint/a11y/useMediaCaption: a spoken report; its transcript is below
            <audio controls preload="none" src={r.audio_url} className="w-full" />
          ) : (
            <p className="text-ui text-ink-3">No recording was attached.</p>
          )}
        </div>
      </Card>
      <Card title="Transcript">
        <div className="px-4 py-3 text-ui">
          {r.transcript ? (
            <p className="whitespace-pre-wrap">{r.transcript}</p>
          ) : r.transcript_status === "failed" ? (
            <p className="text-danger-ink">
              Not transcribed{r.transcript_error ? `: ${r.transcript_error}` : "."}
            </p>
          ) : r.transcript_status === "pending" ? (
            <p className="text-ink-3">Transcribing.</p>
          ) : (
            <p className="text-ink-3">No transcript.</p>
          )}
        </div>
      </Card>
      {r.note ? (
        <Card title="Note">
          <p className="whitespace-pre-wrap px-4 py-3 text-ui">{r.note}</p>
        </Card>
      ) : null}
    </>
  );
}

function EmailBody({ r }: { r: BugReportDetail }) {
  return (
    <div className="px-4 py-3 text-ui">
      {r.email_status === "sent" ? (
        <p>Sent {fmtDateTime(r.email_sent_at)}.</p>
      ) : r.email_status === "not_configured" ? (
        <p className="text-ink-2">Not sent. Set up the email provider under Providers.</p>
      ) : r.email_status === "failed" ? (
        <p className="text-danger-ink">Failed{r.email_error ? `: ${r.email_error}` : "."}</p>
      ) : (
        <p className="text-ink-2">
          Sending{r.email_error ? `. The last attempt failed: ${r.email_error}` : "."}
        </p>
      )}
    </div>
  );
}

const FREQUENCY: Record<BugStructuredReport["frequency"], string> = {
  once: "Happened once",
  sometimes: "Happens sometimes",
  often: "Happens often",
  always: "Happens every time",
  unknown: "Frequency unknown",
};
const COMES_UP: Record<BugStructuredReport["frequency"], string> = {
  once: "Came up once",
  sometimes: "Comes up sometimes",
  often: "Comes up often",
  always: "Comes up all the time",
  unknown: "How often it comes up: not known",
};

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-caption text-ink-2">{label}</span>
      {children}
    </div>
  );
}

function StructuredCard({ report }: { report: BugStructuredReport }) {
  const kind = report.kind ?? "bug";
  return (
    <Card title="Report">
      <div className="flex flex-col gap-3 px-4 py-3 text-ui">
        {kind === "bug" ? (
          <div className="flex flex-wrap gap-1.5">
            <Badge tone="neutral">{report.blocking ? "Blocks work" : "Has a workaround"}</Badge>
            <Badge tone="neutral">{FREQUENCY[report.frequency]}</Badge>
          </div>
        ) : kind === "feature" ? (
          <div className="flex flex-wrap gap-1.5">
            <Badge tone="neutral">{COMES_UP[report.frequency]}</Badge>
          </div>
        ) : null}
        <p className="whitespace-pre-wrap">{report.summary}</p>
        {report.need ? (
          <Labeled label="The need">
            <p className="whitespace-pre-wrap">{report.need}</p>
          </Labeled>
        ) : null}
        {report.why ? (
          <Labeled label="Why it matters">
            <p className="whitespace-pre-wrap">{report.why}</p>
          </Labeled>
        ) : null}
        {report.example ? (
          <Labeled label="Example">
            <p className="whitespace-pre-wrap">{report.example}</p>
          </Labeled>
        ) : null}
        {report.steps.length > 0 ? (
          <Labeled label="Steps">
            <ol className="list-decimal pl-5">
              {report.steps.map((step, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: steps never reorder
                <li key={i}>{step}</li>
              ))}
            </ol>
          </Labeled>
        ) : null}
        {report.expected ? (
          <Labeled label="Expected">
            <p className="whitespace-pre-wrap">{report.expected}</p>
          </Labeled>
        ) : null}
        {report.actual ? (
          <Labeled label="What happened">
            <p className="whitespace-pre-wrap">{report.actual}</p>
          </Labeled>
        ) : null}
        {report.call_ids.length > 0 ? (
          <Labeled label="Calls">
            <span className="flex flex-wrap gap-3">
              {report.call_ids.map((id) => (
                <Link
                  key={id}
                  to="/admin/calls/$callId"
                  params={{ callId: id }}
                  className="font-mono text-caption text-accent-ink hover:underline"
                >
                  {id.slice(0, 8)}
                </Link>
              ))}
            </span>
          </Labeled>
        ) : null}
        {report.contact_refs.length > 0 ? (
          <Labeled label="Contacts">
            <p>{report.contact_refs.join(", ")}</p>
          </Labeled>
        ) : null}
      </div>
    </Card>
  );
}

function ConversationCard({ r }: { r: BugReportDetail }) {
  const name = r.user?.name?.split(" ")[0] ?? "Rep";
  return (
    <Card title={r.modes.includes("voice") ? "Conversation (voice and text)" : "Conversation"}>
      <div className="flex max-h-[560px] flex-col gap-2.5 overflow-y-auto px-4 py-3 text-ui">
        {r.conversation.map((t, i) =>
          t.role === "tool" ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: turns never reorder
            <p key={i} className="text-caption text-ink-3">
              Checked {t.tool_name ?? "a tool"}
            </p>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: turns never reorder
            <div key={i} className="flex flex-col gap-0.5">
              <span className="text-caption text-ink-2">
                {t.role === "user" ? name : "Assistant"}
                <span className="text-ink-3">
                  {" "}
                  {t.mode === "voice" ? "spoke" : "typed"}
                  {t.at ? `, ${fmtDateTime(t.at)}` : ""}
                </span>
              </span>
              {t.role === "user" ? (
                <p className="whitespace-pre-wrap">{t.text}</p>
              ) : (
                <Markdown text={t.text ?? ""} />
              )}
            </div>
          ),
        )}
      </div>
    </Card>
  );
}
